<template>
  <div class="card card-slider mb-3">
    <div class="card-body">
      <div class="row gx-5">
        <div class="col-md-7 text-start">
          <p>{{ upperText }}</p>
          <input
            type="range"
            class="form-range my-3"
            id="annualCharge"
            v-model="annualChargeModel"
            min="0.4"
            max="2"
            step="0.1"
          />
          <p>{{ lowerText }}</p>
        </div>

        <div class="col-md-5 border-start">
          <p class="mb-1"><strong>Annual Charge</strong></p>
          <span class="display-5" v-html="annualChargeText"></span>
          <p class="mb-1 mt-3">Expected charges in retirement</p>
          <span class="fs-5">{{ industryCostText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { formatter } from "@/shared/ts/formatter";
import GMath from "@/shared/ts/gMath";

const props = defineProps({
    industryAverageCharge: { type: Number, required: true },
    upperText: { type: String, default: "" },
    lowerText: { type: String, default: "" }
});

const emit = defineEmits(['charge-rate-changed']);

defineExpose({ invokeChangeEvent });

const annualChargeModel = ref(1);

watch(() => props.industryAverageCharge, () => invokeChangeEvent());

watch(() => annualChargeModel.value, () => {
    emit('charge-rate-changed', annualChargeModel.value, industryCost.value);
});

const industryCost = computed<null | number>(() => {
  const adjustedChargeRate = annualChargeModel.value / 1.5;
  const cost = props.industryAverageCharge * adjustedChargeRate;
  const toNearestHundred = GMath.round(cost, -2);
  return toNearestHundred;
});

const industryCostText = computed<string>(() => {
  return industryCost.value == null ? "" : formatter.formatCurrency(industryCost.value);
});

const annualChargeText = computed<string>(() => {
  const integer = Math.floor(annualChargeModel.value);
  let mod = annualChargeModel.value < 1
      ? annualChargeModel.value
      : annualChargeModel.value % integer;

  let annualCharge = "";

  switch (mod) {
    case 0:
      annualCharge = `${integer.toFixed(0)}.0% `;
      break;
    default:
      mod = GMath.round(mod, 1);
      annualCharge = `${integer.toFixed(0)}.${mod
        .toString()
        .replace("0.", "")}%`;
  }

  // Use &numsp; to stabilise different decimal string lengths, e.g. .replaceAll(" ", "&numsp;");
  return annualCharge;
});

function invokeChangeEvent() {
  emit('charge-rate-changed', annualChargeModel.value, industryCost.value);
}

</script>

<style scoped>
    .card-slider p {
        font-size: 14px !important;
    }
</style>
